import {
  ToolData,
  FormationData,
  ResultsData,
  ToolOptimizationResult
} from '../types/types';

/**
 * Optimize the tools needed based on total hours and tool capabilities
 */
export const optimizeTools = (
  totalHours: number,
  tool1Hours: number,
  tool2Hours: number
): ToolOptimizationResult => {
  const baseTools = Math.floor(totalHours / tool1Hours);
  const remainingHours = totalHours - baseTools * tool1Hours;

  if (remainingHours <= tool1Hours * 0.1) {
    return { primary: baseTools, secondary: 0 };
  } else if (remainingHours <= tool2Hours) {
    return { primary: baseTools, secondary: 1 };
  } else {
    return { primary: baseTools + 1, secondary: 0 };
  }
};

/**
 * Calculate results based on input values
 */
export const calculateResultData = (
  formations: FormationData,
  viperTool: ToolData,
  rhoTool: ToolData,
  convTool: ToolData,
  tripTime: string,
  rigCost: string
): ResultsData => {
  // Calculate footage and hours
  const capFootage = Math.max(
    0,
    parseFloat(formations.capRockEnd) - parseFloat(formations.capRockStart)
  );
  const saltFootage = Math.max(
    0,
    parseFloat(formations.saltEnd) - parseFloat(formations.saltStart)
  );

  // HOC Calculations
  const hocCapHours = capFootage / parseFloat(viperTool.capROP);
  const hocSaltHours = saltFootage / parseFloat(viperTool.saltROP);
  const hocTotalHours = hocCapHours + hocSaltHours;

  // Competitor Calculations
  const compCapHours = capFootage / parseFloat(convTool.capROP);
  const compSaltHours = saltFootage / parseFloat(convTool.saltROP);
  const compTotalHours = compCapHours + compSaltHours;

  // Calculate HOC tool distribution
  const hocTools = optimizeTools(
    hocTotalHours,
    parseFloat(viperTool.hours),
    parseFloat(rhoTool.hours)
  );
  const compTools = Math.ceil(compTotalHours / parseFloat(convTool.hours));

  // Calculate costs
  const tripCost = parseFloat(tripTime) * parseFloat(rigCost);

  const hocToolCosts =
    hocTools.primary * parseFloat(viperTool.cost) +
    hocTools.secondary * parseFloat(rhoTool.cost);

  const hocTripCosts = (hocTools.primary + hocTools.secondary) * tripCost;

  const hocDrillHoursCost = hocTotalHours * parseFloat(rigCost);

  const hocTotalCost = hocToolCosts + hocTripCosts + hocDrillHoursCost;

  const compToolCosts = compTools * parseFloat(convTool.cost);

  const compTripCosts = compTools * tripCost;

  const compDrillHoursCost = compTotalHours * parseFloat(rigCost);

  const compTotalCost = compToolCosts + compTripCosts + compDrillHoursCost;

  const savings = compTotalCost - hocTotalCost;
  const percentageSavings =
    compTotalCost > 0 ? (savings / compTotalCost) * 100 : 0;

  return {
    hoc: {
      capHours: hocCapHours,
      saltHours: hocSaltHours,
      totalHours: hocTotalHours,
      viperTools: hocTools.primary,
      rhoTools: hocTools.secondary,
      totalCost: hocTotalCost,
      toolCosts: hocToolCosts,
      tripCosts: hocTripCosts,
      drillHoursCost: hocDrillHoursCost
    },
    competitor: {
      capHours: compCapHours,
      saltHours: compSaltHours,
      totalHours: compTotalHours,
      convTools: compTools,
      totalCost: compTotalCost,
      toolCosts: compToolCosts,
      tripCosts: compTripCosts,
      drillHoursCost: compDrillHoursCost
    },
    savings: savings,
    percentageSavings: percentageSavings
  };
};

/**
 * Format a number with commas
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Format currency with commas and dollar sign
 */
export const formatCurrency = (num: number): string => {
  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

/**
 * Validate form inputs
 */
export const validateFormInputs = (
  formations: FormationData,
  tripTime: string,
  rigCost: string
): string[] => {
  const errors: string[] = [];

  if (
    !formations.capRockStart ||
    !formations.capRockEnd ||
    !formations.saltStart ||
    !formations.saltEnd
  ) {
    errors.push('All formation depth values are required');
  }

  if (
    parseFloat(formations.capRockStart) >= parseFloat(formations.capRockEnd)
  ) {
    errors.push('Cap Rock ending depth must be greater than starting depth');
  }

  if (parseFloat(formations.saltStart) >= parseFloat(formations.saltEnd)) {
    errors.push('Salt ending depth must be greater than starting depth');
  }

  if (!tripTime || parseFloat(tripTime) <= 0) {
    errors.push('Trip time must be a positive number');
  }

  if (!rigCost || parseFloat(rigCost) <= 0) {
    errors.push('Rig cost must be a positive number');
  }

  return errors;
};
